import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EditNoteIcon from '@mui/icons-material/EditNote';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import HandshakeIcon from '@mui/icons-material/Handshake';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded';
import {
  IntroPanel,
  Eyebrow,
  IntroHeading,
  IntroLead,
  TradeGrid,
  TradeCard,
  TradeBody,
  TradeIcon,
  TradeName,
  TradeHint,
  OtherLink,
  Section,
  SectionTitle,
  StepList,
  Step,
  StepIcon,
  StepTitle,
  StepText,
  SmartCard,
  SmartIcon,
  SmartText,
  SmartTitle,
  SmartBody,
  SmartButton,
} from './ServiceSummary.styled';
import { getPublicServicePickerTrades } from '../../constants/mockData/allTrades';
import { TRADE_EXAMPLE_JOBS } from '../../constants/serviceSearchCatalog';
import { getTradeTheme } from '../../constants/tradeTheme';
import { buildSomethingElsePath, buildTaskStepPath } from '../../utils/serviceSearch';
import { useAuth } from '../../context/AuthContext';
import AuthModal from '../common/AuthModal/AuthModal';
import { trackEvent } from '../../services/api/analyticsService';
import SEO from '../common/SEO/SEO';

const STEPS = [
  {
    key: 'need',
    Icon: EditNoteIcon,
    title: 'Tell us what you need',
    text: 'Pick a trade, then the job and when you need it.',
  },
  {
    key: 'discover',
    Icon: TravelExploreIcon,
    title: 'See local pros',
    text: 'Compare Northern Kentucky businesses that do that work.',
  },
  {
    key: 'connect',
    Icon: HandshakeIcon,
    title: 'Get connected',
    text: 'Contact the pro you pick by phone, text or email.',
  },
];

const trades = getPublicServicePickerTrades().map((trade) => {
  const theme = getTradeTheme(trade.id);
  return {
    id: trade.id,
    title: trade.name,
    hint: TRADE_EXAMPLE_JOBS[trade.id],
    Icon: theme?.icon,
    color: theme?.color ?? '#0d4efe',
  };
});

const ServiceSummary = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [showAuthDialog, setShowAuthDialog] = useState(false);

  const handleGetMatched = () => {
    if (authLoading) return;
    if (!user) {
      setShowAuthDialog(true);
      return;
    }
    navigate('/ai-service-search');
  };

  const handleTradeClick = (tradeId) => {
    trackEvent({
      eventType: 'service_view',
      businessId: 'app',
      serviceShortName: tradeId,
    });
    navigate(buildTaskStepPath(tradeId));
  };

  return (
    <>
      <SEO
        title="Home services"
        description="Browse plumbing, HVAC, electrical, and landscaping services in Northern Kentucky. Tell us what you need and get matched with local providers."
        path="/services"
      />

      <IntroPanel aria-labelledby="services-title">
        <Eyebrow>Find a pro</Eyebrow>
        <IntroHeading id="services-title">What do you need help with?</IntroHeading>
        <IntroLead>
          Choose a trade to see local Northern Kentucky pros who do the job.
        </IntroLead>

        <TradeGrid role="list" aria-label="Trades">
          {trades.map(({ id, title, hint, Icon, color }) => (
            <TradeCard
              key={id}
              role="listitem"
              style={{ '--trade': color }}
              onClick={() => handleTradeClick(id)}
            >
              <TradeBody>
                {Icon ? (
                  <TradeIcon aria-hidden>
                    <Icon />
                  </TradeIcon>
                ) : null}
                <span>
                  <TradeName>{title}</TradeName>
                  {hint ? <TradeHint>{hint}</TradeHint> : null}
                </span>
              </TradeBody>
            </TradeCard>
          ))}
        </TradeGrid>

        <OtherLink onClick={() => navigate(buildSomethingElsePath())}>
          <MoreHorizRoundedIcon aria-hidden />
          Something else? Tell us what you need
          <ArrowForwardRoundedIcon aria-hidden />
        </OtherLink>
      </IntroPanel>

      <Section aria-labelledby="services-steps-title">
        <Eyebrow>How it works</Eyebrow>
        <SectionTitle id="services-steps-title">Three steps to the right pro</SectionTitle>
        <StepList>
          {STEPS.map(({ key, Icon, title, text }) => (
            <Step key={key}>
              <StepIcon aria-hidden>
                <Icon />
              </StepIcon>
              <div>
                <StepTitle>{title}</StepTitle>
                <StepText>{text}</StepText>
              </div>
            </Step>
          ))}
        </StepList>
      </Section>

      <SmartCard>
        <SmartIcon aria-hidden>
          <AutoAwesomeIcon />
        </SmartIcon>
        <SmartText>
          <SmartTitle>Not sure which trade?</SmartTitle>
          <SmartBody>
            We offer more than what's listed here. Describe the job and Smart
            Search will find every service on NKY Connect that fits.
          </SmartBody>
        </SmartText>
        <SmartButton onClick={handleGetMatched}>Get matched</SmartButton>
      </SmartCard>

      <AuthModal
        open={showAuthDialog}
        onClose={() => setShowAuthDialog(false)}
        title="Account Required"
        message="To use this feature, please log in or create an account."
        postAuthRedirectPath="/ai-service-search"
      />
    </>
  );
};

export default ServiceSummary;
